import { useEffect, useRef } from "react"

/**
 * Hook for managing DataGrid event subscriptions for persistence
 *
 * @param {Object} apiRef - DataGrid API reference
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether persistence is enabled
 * @param {boolean} options.isStateLoaded - Whether the initial state has been loaded
 * @param {Function} options.saveState - Function to call when state should be saved
 * @param {Function} options.applyCustomColumnOrder - Function to call when drag ends (switch to custom mode and save order)
 * @returns {Object} Event subscription utilities
 */
export function useEventSubscriptions(apiRef, { enabled, isStateLoaded, saveState, applyCustomColumnOrder }) {
  const eventUnsubscribersRef = useRef([])

  useEffect(() => {
    if (!enabled || !apiRef.current || !isStateLoaded) { return }

    const api = apiRef.current
    const unsubscribers = []

    unsubscribers.push(
      api.subscribeEvent("columnWidthChange", saveState),
      api.subscribeEvent("pinnedColumnsChange", saveState),
      api.subscribeEvent("columnHeaderDragEnd", applyCustomColumnOrder),
    )
    eventUnsubscribersRef.current = unsubscribers

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe())
      eventUnsubscribersRef.current = []
    }
  }, [enabled, apiRef, isStateLoaded, saveState, applyCustomColumnOrder])
}
